// Libs
import React, { useEffect, useState } from 'react';
import { Tooltip, Modal } from 'antd';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import * as Yup from 'yup';

// Own Libs
import { Button, FormDatePicker, FormSelect, FormSubmitButton, MasterDetailForm, Row } from '@digital/ui';

//Api
import { anexarSolicitacaoAvaliacaoFinanceira, ISasPropostaOrdemCompra, ISasPropostaOrdemCompraDocs } from '@app/api/sas/sasProposta';

//Files
import { editandoState, modalAcoesIdState, reloadVendorListState, vendorListDocsState } from '../atoms';
import { UploadFile, Visibility } from '@app/components';

//Utils
import { isObjNotEmpty, vtObjToFormData } from '@app/helpers/objHelper';
import { EStatusSolicitacaoAvaliacaoFinanceira } from '@app/enums/EResultadoEnvioConfide';
import ModalConfirmContentJustificativa from '@app/components/ModalConfirmContent/ModalConfirmContentJustificativa';
import { IArquivo } from '@app/types/core.types';

interface Props {
    sasProposta: ISasPropostaOrdemCompra;
}

export const ModalSolicitacaoAvaliacaoFinanceira = ({ sasProposta }: Props) => {

    const setModalAcoesId = useSetRecoilState(modalAcoesIdState);
    const setReloadVendorList = useSetRecoilState(reloadVendorListState);
    const editando = useRecoilValue(editandoState);

    const vendorListDocs = useRecoilValue(vendorListDocsState);

    const [exibirModal, setExibirModal] = useState(false);
    const [doc, setDoc] = useState<ISasPropostaOrdemCompraDocs>({} as ISasPropostaOrdemCompraDocs);

    useEffect(() => {
        var doc = vendorListDocs.find(f => f.id == sasProposta.id);
        if (isObjNotEmpty(doc)) {
            setDoc(doc || {} as ISasPropostaOrdemCompraDocs);
        }
    }, [exibirModal]);

    const handleCancel = () => {
        setExibirModal(false);
    };

    const handleCancelAndClose = () => {
        setExibirModal(false);
        setModalAcoesId('');
    };

    const handleSuccess = () => {
        setReloadVendorList((v: boolean) => !v);
        handleCancelAndClose();
    };

    const handleSubmit = (values: any) => {
        const form = vtObjToFormData(values);

        if (editando.sasPropostaId == sasProposta.id) {
            Modal.confirm({
                title: <>{editando.justificativa}</>,
                content: <ModalConfirmContentJustificativa
                    texto='É necessário informar a justificativa para a alteração.'
                    setJustificativa={e => {
                        form.set('justificativaEdicao', e);
                    }}
                />,
                okText: 'Confirmar',
                cancelText: 'Cancelar',
                onOk: async (e) => {
                    form.append('editando', 'true');
                    return new Promise((resolve, reject) => {
                        anexarSolicitacaoAvaliacaoFinanceira(sasProposta.id, form)
                            .then((data: any) => {
                                if (data && data.valid) {
                                    handleSuccess();
                                    return resolve(true);
                                } else {
                                    if (data.notifications && data.notifications.length > 0) {
                                        const erroJustificativaEdicao = data.notifications.findIndex((c: any) => {
                                            return c.property == 'justificativaEdicao';
                                        });
                                        if (erroJustificativaEdicao >= 0) {
                                            return reject(false);
                                        }
                                    }
                                }
                            });
                    });
                },
                onCancel: handleCancelAndClose
            });
        } else {
            anexarSolicitacaoAvaliacaoFinanceira(sasProposta.id, form)
                .then((data: any) => {
                    if (data && data.valid) {
                        handleSuccess();
                    }
                });
        }
    };

    return (
        <>
            <Tooltip title="Solicitação da Avaliação Financeira">
                <Button icon='business-documents' onClick={() => { setExibirModal(true); }} />
            </Tooltip>

            <Visibility visible={exibirModal}>

                <Modal title={`Solicitação da Avaliação Financeira`} width='780px' visible={exibirModal} onCancel={handleCancel} footer={null} >
                    <Visibility visible={isObjNotEmpty(sasProposta) && exibirModal} >
                        <MasterDetailForm
                            key={`formEnvioConfide_${sasProposta.id}`}
                            actions={[
                                <Button title='fechar' type='link' onClick={() => handleCancel()} />,
                                <FormSubmitButton title='Salvar' type='primary' noIcon onClick={handleSubmit} />
                            ]}
                            initialValues={sasProposta}
                            validationSchema={
                                Yup.object().shape({
                                    arquivo: isObjNotEmpty(doc) && doc.anexoSolicitacaoAvaliacaoFinanceira ? Yup.mixed().notRequired() : Yup.mixed().required('É necessário anexar um arquivo.'),
                                })
                            }
                        >
                            <Row>
                                <FormSelect
                                    label='Status'
                                    name='statusSolicitacaoAvaliacaoFinanceira'
                                    value={sasProposta.statusSolicitacaoAvaliacaoFinanceira
                                        ? sasProposta.statusSolicitacaoAvaliacaoFinanceira
                                        : EStatusSolicitacaoAvaliacaoFinanceira.PENDENTE_ENVIO
                                    }
                                    options={[
                                        { text: EStatusSolicitacaoAvaliacaoFinanceira.PENDENTE_ENVIO, value: EStatusSolicitacaoAvaliacaoFinanceira.PENDENTE_ENVIO },
                                        { text: EStatusSolicitacaoAvaliacaoFinanceira.ENVIADO, value: EStatusSolicitacaoAvaliacaoFinanceira.ENVIADO },
                                    ]}
                                    span={6}
                                    disabled
                                />

                                <FormDatePicker
                                    format='DD/MM/YYYY'
                                    name='dataStatusSolicitacaoAvaliacaoFinanceira'
                                    label='Data de Envio'
                                    required
                                    span={6} />
                            </Row>
                            <Row>
                                <UploadFile
                                    idDoComponente='arquivos-ModalStatusSolicitacaoAvaliacaoFinanceira'
                                    type='primary'
                                    hasFile={false}
                                    hasLabel={true}
                                    icon='ui-upload'
                                    span={6}
                                    onGetArquivo={() => {
                                        if (isObjNotEmpty(doc.anexoSolicitacaoAvaliacaoFinanceira)) {
                                            return Promise.resolve({
                                                id: doc.anexoSolicitacaoAvaliacaoFinanceira.id,
                                                nome: doc.anexoSolicitacaoAvaliacaoFinanceira.nome,
                                                status: "",
                                                statusArquivoId: doc.anexoSolicitacaoAvaliacaoFinanceira.statusArquivoId || "",
                                            } as IArquivo);
                                        }
                                        return Promise.resolve(undefined);
                                    }}
                                    showDownload={true}
                                />
                            </Row>
                        </MasterDetailForm>
                    </Visibility>
                </Modal >

            </Visibility>
        </>
    );
};